import { AnimatePresence, motion, Reorder, useDragControls } from 'framer-motion'
import { useMemo, useState, type CSSProperties } from 'react'
import type { Item, Store, Trip } from '../../lib/supabase'
import { boughtIn, walkedItems } from '../../lib/trips'
import { haptic } from '../../lib/haptics'
import { HAPTICS } from '../../lib/constants'
import { CostField, type CostWriter } from '../list/TripSummarySheet'
import { ArrowDownIcon, ArrowUpIcon, ChevronIcon, GripIcon } from '../icons'
import type { TripPatch } from '../../hooks/useTripEdits'

type Bought = Pick<Item, 'id' | 'name' | 'category_id' | 'done' | 'done_at' | 'deleted_at'>

const EASE = [0.2, 0.8, 0.2, 1] as const

// Edit trip (046), the trip sheet's second face (TripSheets): what it cost
// first and big — Enter or leaving the field saves — then the store as one
// tap, then the order it was walked in, folded away until asked (dragged; the
// store learns it again), and Delete trip — gone from Stats and from the
// store's learning, its items still in the history. Anyone in the household
// may correct a trip; the summary's cost question is only the finisher's.
export function TripEditor({ trip, rows, stores, tintFor, focusCost, onBack, onEdit, onDelete, onCost, onClose }: {
  trip: Trip
  /** The household's item history: the trip's items are the rows its Finish cleared. */
  rows: Bought[]
  stores: Store[]
  tintFor: (categoryId: string | null) => string
  /** Opened by Add cost: the cost field takes focus. */
  focusCost: boolean
  /** Back to the summary it was opened from; none when opened straight to edit. */
  onBack?: () => void
  onEdit: (trip: Trip, patch: TripPatch) => Promise<boolean>
  onDelete: (trip: Trip) => Promise<boolean>
  onCost: CostWriter
  onClose: () => void
}) {
  const bought = useMemo(() => (trip.finished_at ? boughtIn(trip.finished_at, rows) : []), [trip.finished_at, rows])
  const [order, setOrder] = useState(() => walkedItems(bought, trip.picked_order).map(b => b.id))
  const [savedOrder, setSavedOrder] = useState(order)
  const [storeId, setStoreId] = useState(trip.store_id)
  const [showOrder, setShowOrder] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const byId = useMemo(() => new Map(bought.map(b => [b.id, b])), [bought])
  const changed = order.join() !== savedOrder.join()
  const storeName = stores.find(s => s.id === storeId)?.name ?? 'Somewhere else'
  // As Stats' trip rows say it: "Sun 25 Oct, 21 items. On the watch".
  const facts = [
    trip.finished_at ? new Date(trip.finished_at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : null,
    trip.items != null ? (trip.items === 1 ? '1 item' : `${trip.items} items`) : null,
  ].filter(Boolean).join(', ') + (trip.finished_on === 'watch' ? '. On the watch' : '')

  const moveTo = async (id: string | null) => {
    if (id === storeId) return
    const before = storeId
    setStoreId(id)
    haptic(HAPTICS.chip)
    if (!(await onEdit(trip, { store_id: id }))) { setStoreId(before); setNote('Couldn’t change the store. Try again.') }
  }
  const saveOrder = async () => {
    if (await onEdit(trip, { picked_order: order })) { setSavedOrder(order); setNote('Saved. The store learns this order.') }
    else setNote('Couldn’t save the order. Try again.')
  }
  const remove = async () => {
    if (await onDelete(trip)) onClose()
    else { setConfirmDelete(false); setNote('Couldn’t delete the trip. Try again.') }
  }
  const choices: { id: string | null; name: string }[] = [...stores.map(s => ({ id: s.id, name: s.name })), { id: null, name: 'Somewhere else' }]

  return (
    <div className="ml-trip ml-trip-edit">
      <header className="ml-trip-edit__head">
        {onBack && (
          <button type="button" className="ml-round-btn ml-trip-edit__back" aria-label="Back to the summary" onClick={onBack}>
            <ChevronIcon size={18} />
          </button>
        )}
        <div className="ml-trip-edit__title">
          <span className="ml-label">Edit trip</span>
          <h2 className="ml-display">{storeName}</h2>
          {facts && <span className="ml-caption">{facts}</span>}
        </div>
      </header>

      <CostField tripId={trip.id} onCost={onCost} autoFocus={focusCost}
        initial={trip.cost != null && trip.currency ? { cost: trip.cost, currency: trip.currency } : null} />

      <section className="ml-trip-edit__field" aria-label="Store">
        <h3 className="ml-label">Store</h3>
        <div className="ml-trip-edit__stores">
          {choices.map(c => (
            <button key={c.id ?? 'none'} type="button" className="ml-trip-edit__store" aria-pressed={c.id === storeId}
              onClick={() => void moveTo(c.id)}>{c.name}</button>
          ))}
        </div>
      </section>

      {order.length > 1 && (
        <section className="ml-trip-edit__field" aria-label="Order walked">
          <button type="button" className="ml-trip-edit__disclose" aria-expanded={showOrder} onClick={() => setShowOrder(o => !o)}>
            <span className="ml-label">Order walked</span>
            <span className="ml-caption">{order.length} items</span>
            <ChevronIcon size={16} />
          </button>
          <AnimatePresence initial={false}>
            {showOrder && (
              <motion.div key="order" className="ml-trip-edit__fold"
                initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: EASE }}>
                <p className="ml-caption">Drag the items into the order you walked the store. It learns from it.</p>
                <Reorder.Group axis="y" values={order} onReorder={setOrder} className="ml-trip-edit__order">
                  {order.map((id, i) => {
                    const item = byId.get(id)
                    return item ? <OrderRow key={id} id={id} name={item.name} tint={tintFor(item.category_id)}
                      index={i} count={order.length} onMove={to => setOrder(o => moveItem(o, i, to))} /> : null
                  })}
                </Reorder.Group>
                {changed && (
                  <button type="button" className="ml-btn ml-btn--primary ml-btn--wide ml-trip-edit__save" onClick={() => void saveOrder()}>
                    Save order
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      )}

      {note && <p className="ml-caption" role="status">{note}</p>}

      <button type="button" className="ml-btn ml-btn--secondary ml-btn--wide" onClick={onClose}>Done</button>
      {confirmDelete ? (
        <button type="button" className="ml-text-btn ml-text-btn--destructive" onClick={() => void remove()}>
          Tap again to delete. It leaves your stats; the items stay in your history.
        </button>
      ) : (
        <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={() => setConfirmDelete(true)}>Delete trip</button>
      )}
    </div>
  )
}

const moveItem = (ids: string[], from: number, to: number) => {
  if (to < 0 || to >= ids.length) return ids
  const next = [...ids]
  const [id] = next.splice(from, 1)
  next.splice(to, 0, id)
  return next
}

/** One item: dragged by its handle (the sheet still scrolls), or moved a step with the buttons. */
function OrderRow({ id, name, tint, index, count, onMove }: {
  id: string
  name: string
  tint: string
  index: number
  count: number
  onMove: (to: number) => void
}) {
  const controls = useDragControls()
  return (
    <Reorder.Item value={id} dragListener={false} dragControls={controls} className="ml-trip-edit__row"
      style={{ '--ml-tint': tint } as CSSProperties}>
      <span className="ml-trip-edit__handle" aria-hidden="true" onPointerDown={e => controls.start(e)}><GripIcon /></span>
      <span className="ml-dot" aria-hidden="true" />
      <span className="ml-trip-edit__name">{name}</span>
      <button type="button" className="ml-text-btn" aria-label={`Move ${name} up`} disabled={index === 0}
        onClick={() => onMove(index - 1)}><ArrowUpIcon /></button>
      <button type="button" className="ml-text-btn" aria-label={`Move ${name} down`} disabled={index === count - 1}
        onClick={() => onMove(index + 1)}><ArrowDownIcon /></button>
    </Reorder.Item>
  )
}
